/**
 * Sidebar partitioning (RFC 0267 S3): split a project's catalog into the two
 * things the sidebar shows — APPS (a first-class surface, opened by name) and
 * FILES (plain documents). An app's own view and skill documents are its
 * internals, and app instances are app data, so both are excluded from Files.
 *
 * Pure over a raw project-doc snapshot (as useDocument hands it over), so it is
 * unit-testable without a repo, React, or wasm.
 */

import {
  type AppSummary,
  type DocumentRef,
  installedApps,
  projectDocuments,
  sessionTitleOf,
} from '@mod/core/model'
import { webFileFromId } from '../../features/web/webPage'
import { codeFileFromId } from '../../services/code/codeFile'
import { instancesAreOpenable } from './resolveSubject'
import type { Subject } from './subject'

export interface SidebarItems {
  apps: AppSummary[]
  files: DocumentRef[]
}

export function partitionSidebarItems(
  projectDoc: unknown,
  /**
   * Document ids the World lists as SKILLS (RFC 0283 S4). They are shown in the
   * Skills folder, so they are hidden here for the same reason an app's
   * internals are: one thing, listed once. Omitted by a caller with no World to
   * hand, which lists them as ordinary files rather than losing them.
   */
  worldSkillDocIds: ReadonlySet<string> = new Set(),
  /**
   * The World, whose apps are every project's (RFC 0338 3g). A record whose
   * app is installed there lists here, and that app's view and skill stay
   * hidden in the project that holds them.
   */
  worldDoc?: unknown,
  /** The World exists but has not loaded: a record with no app yet is not
   *  listed, since its app may be the World's (RFC 0338 S7). */
  worldLoading = false,
): SidebarItems {
  const apps = installedApps(worldDoc, projectDoc)
  // Every app's view + skill doc is an app internal, hidden from Files.
  const appInternalIds = new Set(
    apps.flatMap((a) => [a.viewDocId, a.skillDocId]),
  )
  const documents = projectDocuments(projectDoc)
  const viewPaths = currentViewPaths(documents, apps)
  const files = documents.filter(
    (d) =>
      !worldSkillDocIds.has(d.id) &&
      isBrowsableDocument(d, apps, appInternalIds, viewPaths, !worldLoading),
  )
  return { apps: apps.filter(isAppRow), files }
}

/**
 * Is this catalog entry an APP a person opens, rather than a package that
 * draws part of the window (RFC 0338 S6)?
 *
 * The World's catalog holds chrome fills, thread renderers and layouts beside
 * its apps. They are installed, but they are not places: a sidebar fill opened
 * as a pane is module source drawn as a page. They are still in the full list
 * above, so their internals stay hidden from Documents.
 */
export function isAppRow(app: AppSummary): boolean {
  if (app.presentation === 'layout') return false
  if (app.handles?.includes('agent')) return false
  // A chrome fill owns no records and contributes a slot. An app with a side
  // column section (RFC 0273) has a data type, so it stays.
  if (!app.dataType && (app.panels?.length ?? 0) > 0) return false
  return true
}

/**
 * Should this document appear in a list of documents (RFC 0271)?
 *
 * Exported because two places ask: the sidebar and the ⌘T palette. They had the
 * same predicate written out twice, which is how they came to disagree.
 *
 * An app's view and skill are its internals and never appear. An INSTANCE
 * appears when its app says its records are openable — a `document` app, whose
 * instances ARE the documents and would be unreachable otherwise, or an
 * `application` that declared `instancesOpenable` and so keeps its index while
 * its records are also addressable on their own.
 *
 * Everything else stays hidden: an `application`'s records are its private data,
 * and a project whose Files list is one row per calendar event has lost the
 * documents somebody wrote.
 */
export function isBrowsableDocument(
  doc: DocumentRef,
  apps: AppSummary[],
  appInternalIds: Set<string>,
  /** From `currentViewPaths`. Empty means "hide no artifacts", which is the
   *  safe direction for a caller that has not got the catalog to hand. */
  viewPaths: ReadonlySet<string> = new Set(),
  /** False while some apps may not have loaded (the World's): a record with
   *  no app is then held back rather than listed as missing one. */
  appsComplete = true,
): boolean {
  if (appInternalIds.has(doc.id)) return false
  // A view document an app no longer points at drops out of appInternalIds and
  // would appear here as an ordinary HTML file. `mod app push` now republishes
  // the view IN PLACE, so this no longer happens on every build — it remains
  // for projects published before that, and it is why the rule is a
  // recogniser rather than a list somebody has to maintain.
  if (isSupersededArtifact(doc, apps, viewPaths)) return false
  if (!doc.dataType) return true
  const owner = apps.find((a) => a.dataType === doc.dataType)
  // A record with no app anywhere lists anyway (RFC 0338 S7) and opens to a
  // prompt to install one: hiding it made a moved week simply vanish.
  return owner ? instancesAreOpenable(owner) : appsComplete
}

/** Opening an app opens its VIEW doc, titled by the app NAME so the canvas and
 *  breadcrumbs read "Polls". The view is always HTML (that is the branch
 *  DocumentView renders as an app), so the mime is fixed rather than looked up. */
export const appAsFile = (app: AppSummary): Subject => ({
  id: app.viewDocId,
  title: app.name,
  path: app.name,
  mime: 'text/html',
  appId: app.id,
})

export const docAsFile = (d: DocumentRef): Subject => ({
  id: d.id,
  title: d.title,
  path: d.path,
  mime: d.mime,
})

export interface SessionSummary {
  id: string
  title: string
  /** The branch it works on, mirrored into the registry (RFC 0277 S12). */
  branch?: string
}

/**
 * A row in Home. A session is not an Subject — it does not go to the canvas —
 * so the union is over WHERE the row leads, which is the only thing a click
 * handler needs to know about it.
 */
export type HomeItem =
  | {
      kind: 'file' | 'app' | 'web'
      id: string
      file: Subject
      /** The project that holds it, when not this window's (RFC 0357): the
       *  pane resolves against that project's catalog. */
      projectId?: string
    }
  | { kind: 'session'; id: string; title: string }

/** The project's sessions, from the RFC 0233 registry (`sessionIds` +
 *  `sessionMeta`) — membership and titles, no session hydration. */
export function projectSessions(projectDoc: unknown): SessionSummary[] {
  const doc = projectDoc as
    | {
        sessionIds?: string[]
        sessionMeta?: Record<
          string,
          {
            title?: string
            titleSetBy?: string
            agentTitle?: string
            branch?: string
          }
        >
      }
    | undefined
    | null
  if (!doc) return []
  return (doc.sessionIds ?? []).map((raw) => {
    const id = String(raw)
    const meta = doc.sessionMeta?.[id]
    const branch = meta?.branch ? String(meta.branch) : undefined
    return {
      id,
      title: sessionTitleOf(meta as never) || 'Untitled session',
      ...(branch ? { branch } : {}),
    }
  })
}

/**
 * Home's rows: the open ids, in the order they were opened, resolved to the
 * things they name.
 *
 * Ids only go in, which is what makes Home self-cleaning: a document that was
 * deleted, a session that was removed, an app that was uninstalled all resolve
 * to nothing and drop out, rather than leaving a row that opens a blank canvas.
 * A WEB id is the exception and needs no catalog — it carries its own url — so a
 * page stays openable regardless of what the project knows about.
 *
 * Pure, so the ordering and the drop-out rule are testable without a repo.
 */
export function resolveHomeItems(
  openIds: string[],
  catalog: {
    apps: AppSummary[]
    documents: DocumentRef[]
    sessions: SessionSummary[]
    /** False on a host that cannot embed a page (the web client): pages drop out
     *  of Home entirely rather than offering a row that opens a blank canvas. */
    web?: boolean
    /** Which project holds an id from another project's catalog. */
    owners?: ReadonlyMap<string, string>
  },
): HomeItem[] {
  const embedWeb = catalog.web !== false
  const byId = new Map<string, HomeItem>()
  for (const app of catalog.apps)
    byId.set(app.viewDocId, {
      kind: 'app',
      id: app.viewDocId,
      file: appAsFile(app),
    })
  // A record carries the app that owns its type, so its row draws that app's
  // tile rather than a document glyph (RFC 0332 S5).
  const ownerOf = new Map<string, string>()
  for (const app of catalog.apps)
    if (app.dataType && !ownerOf.has(app.dataType))
      ownerOf.set(app.dataType, app.id)
  for (const d of catalog.documents) {
    const appId = d.dataType ? ownerOf.get(d.dataType) : undefined
    byId.set(d.id, {
      kind: 'file',
      id: d.id,
      file: appId ? { ...docAsFile(d), appId } : docAsFile(d),
    })
  }
  for (const s of catalog.sessions)
    byId.set(s.id, { kind: 'session', id: s.id, title: s.title })

  const out: HomeItem[] = []
  for (const id of openIds) {
    const web = embedWeb ? webFileFromId(id) : null
    if (web) {
      out.push({ kind: 'web', id, file: web })
      continue
    }
    // A project file (RFC 0277 S6) is a file row, resolved from its id alone.
    const code = codeFileFromId(id)
    if (code) {
      out.push({ kind: 'file', id, file: code })
      continue
    }
    const hit = byId.get(id)
    const owner = catalog.owners?.get(id)
    if (hit)
      out.push(
        hit.kind !== 'session' && owner ? { ...hit, projectId: owner } : hit,
      )
  }
  return out
}
/**
 * Where each app's CURRENT view document sits, by path.
 *
 * Built once and handed to both callers, because the superseded-artifact rule
 * is asked in two places (the sidebar and the ⌘K palette) and a predicate
 * written out twice is how those two came to disagree in the first place.
 */
export function currentViewPaths(
  documents: DocumentRef[],
  apps: AppSummary[],
): Set<string> {
  const viewIds = new Set(apps.map((a) => a.viewDocId))
  return new Set(
    documents.filter((d) => viewIds.has(d.id) && d.path).map((d) => d.path),
  )
}

/**
 * A build artifact an app no longer points at.
 *
 * Recognised rather than tracked, because the alternative is a list of every
 * view document an app has ever had, which grows forever and must be written
 * correctly by every publisher.
 *
 * The recogniser is PATH IDENTITY: a leftover is the html document sitting
 * exactly where a live view sits while not being one. That was every republish
 * before `mod app push` began publishing the view in place, and it is still
 * every project built that way.
 *
 * An ADOPT cutover is deliberately NOT caught: it mints a fresh view at the new
 * project's path and leaves the old one where it was, precisely so rolling back
 * is one `updateApp({viewDocId})` away. A document kept for rollback is one you
 * want to be able to find.
 *
 * That, rather than "somewhere under apps/", because a false positive HIDES a
 * document someone wrote, which is much worse than showing a stale artifact —
 * one is invisible, the other is merely untidy. A note written next to an app's
 * source is an ordinary file and must stay listed; only a document at a live
 * view's own path can be a replaced copy of it.
 */
export function isSupersededArtifact(
  doc: DocumentRef,
  apps: AppSummary[],
  viewPaths: ReadonlySet<string>,
): boolean {
  if (doc.dataType) return false
  if (doc.mime !== 'text/html') return false
  // The view an app points at NOW is hidden as an internal, not as an artifact.
  if (apps.some((a) => a.viewDocId === doc.id)) return false
  return viewPaths.has(doc.path)
}
